import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { catchError, debounceTime, forkJoin, map, Observable, of, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { FUNNEL_TYPE } from '../../../enum/constants';
import { BoardService } from '../../../services/board.service';
import { TaskService } from '../../../services/task.service';
import { BoardFunnel } from '../../board-v3/board-v3.types';
import { ResolvedTaskLink, TaskLinkTarget } from '../tasks.types';
import { getLinkKey, getLinkTypeKey, TASK_LINK_ICONS } from '../tasks.utils';

let nextLinksId = 0;

/**
 * The entities a task is about: leads, deals, contacts and companies. It is a form control, its value is the
 * list of the links with the names of what they point to. New ones are found by name in a search that opens
 * under the field; the arrow keys walk through the results and Enter picks one.
 */
@Component({
  selector: 'app-task-links',
  templateUrl: './task-links.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => TaskLinksComponent) }],
})
export class TaskLinksComponent implements ControlValueAccessor, OnInit {
  /** Whether a click on a link opens what it points to; the form of a new task has nothing to open yet */
  @Input() openable = false;
  /** Off while a card detail is open: the card links are shown, but they do not open a second card detail */
  @Input() canOpenCards = true;
  @Output() openLink = new EventEmitter<ResolvedTaskLink>();
  @ViewChild('origin') private origin?: ElementRef<HTMLElement>;

  private readonly taskService = inject(TaskService);
  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uid = nextLinksId++;
  private readonly search$ = new Subject<string>();
  private funnels$?: Observable<BoardFunnel[]>;
  private onChange: (links: ResolvedTaskLink[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected readonly icons = TASK_LINK_ICONS;
  protected readonly getLinkKey = getLinkKey;
  protected readonly getLinkTypeKey = getLinkTypeKey;
  protected readonly listboxId = `task-links-${this.uid}-listbox`;
  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected links: ResolvedTaskLink[] = [];
  protected disabled = false;
  protected query = '';
  protected results: TaskLinkTarget[] = [];
  protected searching = false;
  protected open = false;
  protected overlayWidth = 0;
  /** The result the arrow keys stand on, none (-1) until a key moves to one */
  protected activeIndex = -1;

  /** The results the task is not linked to yet */
  protected get freshResults(): TaskLinkTarget[] {
    const linked = new Set(this.links.map(getLinkKey));
    return this.results.filter((target) => !linked.has(getLinkKey(target)));
  }

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        tap((search) => (this.searching = !!search)),
        // An empty text is a search too: it drops the one that is still running
        switchMap((search) =>
          search
            ? this.getFunnels().pipe(
                switchMap((funnels) => this.taskService.searchLinkTargets(search, funnels)),
                catchError(() => of([] as TaskLinkTarget[])),
              )
            : of([] as TaskLinkTarget[]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.results = results;
        this.activeIndex = -1;
        this.searching = false;
      });
  }

  writeValue(links: ResolvedTaskLink[] | null): void {
    this.links = links ?? [];
  }

  registerOnChange(fn: (links: ResolvedTaskLink[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  protected optionId(index: number): string {
    return `task-links-${this.uid}-option-${index}`;
  }

  protected canOpen(link: ResolvedTaskLink): boolean {
    return this.openable && !link.missing && (link.entity_type !== 'card' || this.canOpenCards);
  }

  protected remove(link: ResolvedTaskLink): void {
    this.links = this.links.filter((item) => getLinkKey(item) !== getLinkKey(link));
    this.emit();
  }

  protected select(target: TaskLinkTarget): void {
    const link: ResolvedTaskLink = {
      entity_type: target.entity_type,
      entity_id: target.entity_id,
      name: target.name,
      funnelType: target.funnelType,
    };
    this.links = [...this.links, link];
    this.reset();
    this.emit();
  }

  protected onQueryChange(value: string): void {
    this.query = value;
    this.activeIndex = -1;
    const search = value.trim();
    if (!search) {
      this.reset();
      return;
    }

    this.openOverlay();
    this.searching = true;
    this.search$.next(search);
  }

  protected onFocus(): void {
    if (this.query.trim()) this.openOverlay();
  }

  protected onKeydown(event: KeyboardEvent): void {
    const results = this.freshResults;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.query.trim()) return;

        event.preventDefault();
        if (!this.open) this.openOverlay();
        if (!results.length) return;

        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex =
          this.activeIndex < 0
            ? step > 0
              ? 0
              : results.length - 1
            : (this.activeIndex + step + results.length) % results.length;
        this.scrollToActive();
        return;
      }
      case 'Enter': {
        // The field can sit in a form: Enter only picks the result, it never sends it
        event.preventDefault();
        const target = this.open ? results[this.activeIndex] : undefined;
        if (target) this.select(target);
        return;
      }
      case 'Escape':
        // The first Escape closes the results, the next one the sidebar the field is in
        if (this.open) {
          this.open = false;
          event.stopPropagation();
        }
        return;
    }
  }

  protected onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.origin?.nativeElement.contains(target)) return;

    this.open = false;
    this.onTouched();
  }

  private openOverlay(): void {
    this.overlayWidth = this.origin?.nativeElement.getBoundingClientRect().width ?? 0;
    this.open = true;
  }

  /** Back to an empty field; a search that is waiting or running is dropped, so it can not bring its results back */
  private reset(): void {
    this.query = '';
    this.results = [];
    this.activeIndex = -1;
    this.searching = false;
    this.open = false;
    this.search$.next('');
  }

  /** The overlay is outside of this component and scrolls on its own, so the active result is brought into view */
  private scrollToActive(): void {
    const id = this.optionId(this.activeIndex);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'nearest' }));
  }

  private emit(): void {
    this.onChange(this.links);
    this.onTouched();
  }

  /** The funnels of leads and deals are asked once, when the first search needs them */
  private getFunnels(): Observable<BoardFunnel[]> {
    this.funnels$ ??= forkJoin([
      this.boardService.getFunnels({ type: FUNNEL_TYPE.LEAD }).pipe(catchError(() => of([] as BoardFunnel[]))),
      this.boardService.getFunnels({ type: FUNNEL_TYPE.DEAL }).pipe(catchError(() => of([] as BoardFunnel[]))),
    ]).pipe(
      map((groups) => groups.flat()),
      shareReplay(1),
    );
    return this.funnels$;
  }
}
